import { useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import { api, type CommissionPaymentRun, type CommissionCollections } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { fmtCurrency, fmtMonth, currentMonth } from '../lib/fmt';
import {
  ActionButton, Badge, Card, CardHeader, EmptyState, SectionLabel, Select, Spinner, Table, Td, Th, Tr,
} from '../components/ui';

const hint: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)' };

/** The last N months, newest first — commission periods are billed months. */
function recentMonths(count = 24): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function statusBadge(status: CommissionPaymentRun['status']) {
  switch (status) {
    case 'paid':     return <Badge variant="green">Paid</Badge>;
    case 'approved': return <Badge variant="blue">Approved</Badge>;
    case 'locked':   return <Badge variant="locked">Locked</Badge>;
    default:         return <Badge variant="amber">Open</Badge>;
  }
}

// ─── Payment run ─────────────────────────────────────────────────────────────

function PaymentRun({ month }: { month: string }) {
  const runQuery = useQuery(() => api.admin.commissionReport.paymentRun(month), [month]);
  const [expanded, setExpanded] = useState(false);
  const data = runQuery.data;

  /** The payment-run export: one line per rep, which is what a payment run
   *  actually needs. Mirrors the payroll CSV on the bonus report. */
  function exportPaymentCsv() {
    if (!data) return;
    const rows: Array<Array<unknown>> = [[
      'Rep', 'Email', 'Period', 'Invoiced services', 'Deposit applied', 'Commission',
    ]];
    for (const r of data.reps) {
      if (r.commission_amount === 0) continue;
      rows.push([
        r.owner_name ?? r.hubspot_owner_id,
        r.owner_email ?? '',
        month,
        r.commissionable_amount.toFixed(2),
        r.deposit_credit_amount.toFixed(2),
        r.commission_amount.toFixed(2),
      ]);
    }
    downloadCsv(`commission-payment-run-${month}.csv`, rows);
  }

  /** Every underlying row, for anyone who needs to check the arithmetic. */
  function exportDetailCsv() {
    if (!data) return;
    const rows: Array<Array<unknown>> = [[
      'Rep', 'Email', 'Period', 'Invoice', 'Account', 'Deal', 'Deal type',
      'Billed', 'Close date', 'Window ends', 'Services billed', 'Deposit applied', 'Commissionable',
      'Split %', 'Rate %', 'Rate source', 'Commission',
    ]];
    for (const r of data.rows) {
      rows.push([
        r.owner_name ?? r.hubspot_owner_id, r.owner_email ?? '', month,
        r.invoice_number ?? r.chargeover_invoice_id, r.account_name ?? r.tempo_account_id ?? '',
        r.deal_name ?? r.hubspot_deal_id, r.deal_type ?? '',
        r.billed_date, r.deal_close_date, r.window_end_date,
        r.gross_services_amount.toFixed(2), r.deposit_credit_amount.toFixed(2),
        r.commissionable_amount.toFixed(2), (r.split_percentage * 100).toFixed(2),
        r.rate_pct, r.rate_source, r.commission_amount.toFixed(2),
      ]);
    }
    downloadCsv(`commission-detail-${month}.csv`, rows);
  }

  if (runQuery.error) return <Card><EmptyState message={runQuery.error} /></Card>;
  if (runQuery.isLoading || !data) return <Card><Spinner /></Card>;

  const hasDeposits = data.total_deposit_credit > 0;

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Payment run — {fmtMonth(month)}</span>
          <span style={{ marginLeft: 8 }}>{statusBadge(data.status)}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <ActionButton onClick={exportDetailCsv} disabled={data.rows.length === 0}>Full CSV</ActionButton>
            <ActionButton variant="primary" onClick={exportPaymentCsv} disabled={data.reps.length === 0}>
              Payment CSV
            </ActionButton>
          </div>
        </CardHeader>

        {data.status === 'open' && (
          <div style={{ padding: '8px 16px', ...hint }}>
            This period is still open, so these figures are rebuilt on every data refresh and can
            still move. Lock it on Period management before paying against it.
          </div>
        )}

        {/* Only worth a column when a deposit was actually drawn down this month. */}
        {data.total_deposit_credit > 0 && (
          <div style={{ padding: '8px 16px', ...hint }}>
            {fmtCurrency(data.total_deposit_credit)} of invoiced services this month was settled by
            drawing down a deposit. That money earned commission when the deposit was invoiced, so
            it is netted off here rather than paid a second time.
          </div>
        )}

        {data.reps.length === 0 ? (
          <EmptyState message="No commission earned in this period." />
        ) : (
          <Table>
            <thead><tr>
              <Th sticky>Rep</Th>
              <Th right>Invoices</Th>
              <Th right>Invoiced services</Th>
              {hasDeposits && <Th right>Deposit applied</Th>}
              <Th right>Commission</Th>
            </tr></thead>
            <tbody>
              {data.reps.map((r) => (
                <Tr key={r.hubspot_owner_id}>
                  <Td bold>{r.owner_name ?? r.hubspot_owner_id}</Td>
                  <Td right muted>{r.invoice_count}</Td>
                  <Td right muted>{fmtCurrency(r.commissionable_amount)}</Td>
                  {hasDeposits && (
                    <Td right muted>
                      {r.deposit_credit_amount === 0 ? '—' : `−${fmtCurrency(r.deposit_credit_amount)}`}
                    </Td>
                  )}
                  <Td right bold>{fmtCurrency(r.commission_amount)}</Td>
                </Tr>
              ))}
              <Tr>
                <Td bold>Total</Td>
                <Td right muted>—</Td>
                {/* Distinct invoices: a split deal bills once but pays several reps. */}
                <Td right bold>{fmtCurrency(data.total_commissionable)}</Td>
                {hasDeposits && <Td right bold>−{fmtCurrency(data.total_deposit_credit)}</Td>}
                <Td right bold>{fmtCurrency(data.total_commission)}</Td>
              </Tr>
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Detail — {data.rows.length} row{data.rows.length === 1 ? '' : 's'}</span>
          <div style={{ marginLeft: 'auto' }}>
            <ActionButton onClick={() => setExpanded((v) => !v)}>{expanded ? 'Hide' : 'Show'}</ActionButton>
          </div>
        </CardHeader>
        {expanded && (data.rows.length === 0 ? <EmptyState message="Nothing to show." /> : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead><tr>
                <Th sticky>Rep</Th>
                <Th>Account</Th>
                <Th>Invoice</Th>
                <Th right>Billed</Th>
                <Th right>Services billed</Th>
                <Th right>Deposit applied</Th>
                <Th right>Commissionable</Th>
                <Th right>Split</Th>
                <Th right>Rate</Th>
                <Th right>Commission</Th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <Tr key={`${r.chargeover_invoice_id}-${r.hubspot_owner_id}`}>
                    <Td bold>{r.owner_name ?? r.hubspot_owner_id}</Td>
                    <Td muted>{r.account_name ?? r.tempo_account_id ?? '—'}</Td>
                    <Td muted>{r.invoice_number ?? r.chargeover_invoice_id}</Td>
                    <Td right muted>{r.billed_date}</Td>
                    <Td right muted>{fmtCurrency(r.gross_services_amount)}</Td>
                    <Td right muted>
                      {r.deposit_credit_amount === 0 ? '—' : `−${fmtCurrency(r.deposit_credit_amount)}`}
                    </Td>
                    <Td right>{fmtCurrency(r.commissionable_amount)}</Td>
                    <Td right muted>{(r.split_percentage * 100).toFixed(r.split_percentage * 100 % 1 === 0 ? 0 : 2)}%</Td>
                    <Td right muted>
                      {r.rate_pct}%
                      {r.rate_source === 'override' && <Badge variant="blue">override</Badge>}
                    </Td>
                    <Td right bold>{fmtCurrency(r.commission_amount)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        ))}
      </Card>
    </>
  );
}

// ─── Collections ─────────────────────────────────────────────────────────────

function Collections({ from, to, setFrom, setTo }: {
  from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void;
}) {
  const query = useQuery(() => api.admin.commissionReport.collections(from, to), [from, to]);
  const data: CommissionCollections | null = query.data ?? null;

  function exportCsv() {
    if (!data) return;
    const rows: Array<Array<unknown>> = [[
      'Invoice', 'Account', 'Billed', 'Invoice total', 'Invoiced services',
      'Commission', 'Collected', 'Deposit credit', 'Collected %', 'Commission collected', 'Last payment',
    ]];
    for (const i of data.invoices) {
      rows.push([
        i.invoice_number ?? i.chargeover_invoice_id, i.account_name ?? '', i.billed_date,
        i.invoice_total.toFixed(2), i.commissionable_amount.toFixed(2), i.commission_amount.toFixed(2),
        i.collected_amount.toFixed(2), i.deposit_credit_amount.toFixed(2),
        (i.collected_ratio * 100).toFixed(1), i.commission_collected.toFixed(2), i.last_payment_date ?? '',
      ]);
    }
    downloadCsv(`commission-collections-${from}_${to}.csv`, rows);
  }

  const pct = data && data.totals.commission_amount > 0
    ? (data.totals.commission_collected / data.totals.commission_amount) * 100
    : 0;

  return (
    <>
      <Card style={{ marginBottom: 14 }}>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Collections</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={hint}>Billed between</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              style={{ fontSize: 12, padding: '3px 5px', border: '0.5px solid var(--color-border)', borderRadius: 4, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
            <span style={hint}>and</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              style={{ fontSize: 12, padding: '3px 5px', border: '0.5px solid var(--color-border)', borderRadius: 4, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
            <ActionButton onClick={exportCsv} disabled={!data || data.invoices.length === 0}>CSV</ActionButton>
          </div>
        </CardHeader>

        <div style={{ padding: '8px 16px', ...hint }}>
          Invoices are selected by the date they were <strong>billed</strong>; collection is counted
          to date, so a recent range legitimately shows a low percentage. Collected counts payments
          only — a deposit credit isn't new cash, since that money was collected on the deposit
          invoice, so it's shown separately. Commission collected is pro-rata: payments settle an
          invoice, not a line.
        </div>

        {query.error ? <EmptyState message={query.error} />
          : query.isLoading || !data ? <Spinner />
          : (
            <Table>
              <thead><tr>
                <Th sticky>Rep</Th>
                <Th right>Commission</Th>
                <Th right>Collected</Th>
                <Th right>Outstanding</Th>
              </tr></thead>
              <tbody>
                {data.reps.map((r) => (
                  <Tr key={r.hubspot_owner_id}>
                    <Td bold>{r.owner_name ?? r.hubspot_owner_id}</Td>
                    <Td right>{fmtCurrency(r.commission_amount)}</Td>
                    <Td right bold>{fmtCurrency(r.commission_collected)}</Td>
                    <Td right muted>{fmtCurrency(r.commission_amount - r.commission_collected)}</Td>
                  </Tr>
                ))}
                <Tr>
                  <Td bold>Total — {pct.toFixed(0)}% collected</Td>
                  <Td right bold>{fmtCurrency(data.totals.commission_amount)}</Td>
                  <Td right bold>{fmtCurrency(data.totals.commission_collected)}</Td>
                  <Td right muted>{fmtCurrency(data.totals.commission_amount - data.totals.commission_collected)}</Td>
                </Tr>
              </tbody>
            </Table>
          )}
      </Card>

      <Card>
        <CardHeader><span style={{ fontSize: 12, fontWeight: 500 }}>By invoice</span></CardHeader>
        {query.isLoading || !data ? <Spinner /> : data.invoices.length === 0 ? (
          <EmptyState message="No commissioned invoices billed in this range." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <thead><tr>
                <Th sticky>Invoice</Th>
                <Th>Account</Th>
                <Th right>Billed</Th>
                <Th right>Invoiced services</Th>
                <Th right>Commission</Th>
                <Th right>Collected</Th>
                <Th right>Deposit credit</Th>
                <Th right>Commission collected</Th>
              </tr></thead>
              <tbody>
                {data.invoices.map((i) => (
                  <Tr key={i.chargeover_invoice_id}>
                    <Td bold>{i.invoice_number ?? i.chargeover_invoice_id}</Td>
                    <Td muted>{i.account_name ?? '—'}</Td>
                    <Td right muted>{i.billed_date}</Td>
                    <Td right>{fmtCurrency(i.commissionable_amount)}</Td>
                    <Td right>{fmtCurrency(i.commission_amount)}</Td>
                    <Td right>
                      {fmtCurrency(i.collected_amount)}
                      <span style={{ ...hint, marginLeft: 4 }}>{(i.collected_ratio * 100).toFixed(0)}%</span>
                    </Td>
                    <Td right muted>{i.deposit_credit_amount === 0 ? '—' : fmtCurrency(i.deposit_credit_amount)}</Td>
                    <Td right bold>{fmtCurrency(i.commission_collected)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function yearAgo(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export default function AdminCommissionReportPage() {
  const [view, setView] = useUrlState('view', 'payment');
  const [month, setMonth] = useUrlState('month', currentMonth());
  const [from, setFrom] = useUrlState('from', yearAgo());
  const [to, setTo] = useUrlState('to', new Date().toISOString().slice(0, 10));

  const months = useMemo(() => recentMonths(), []);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Sales commissions</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {view === 'payment' && (
            <Select value={month} onChange={setMonth}>
              {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </Select>
          )}
          <ActionButton variant={view === 'payment' ? 'primary' : 'default'} onClick={() => setView('payment')}>
            Payment run
          </ActionButton>
          <ActionButton variant={view === 'collections' ? 'primary' : 'default'} onClick={() => setView('collections')}>
            Collections
          </ActionButton>
        </span>
      </div>

      {view === 'collections'
        ? <Collections from={from} to={to} setFrom={setFrom} setTo={setTo} />
        : <PaymentRun month={month} />}
    </>
  );
}
